import {
  CreateButton,
  Datagrid,
  DateField,
  ExportButton,
  FunctionField,
  List,
  Pagination,
  TopToolbar,
  useRecordContext,
} from "react-admin";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { RaRowActions } from "../../components/RaRowActions";
import { AppBulkDeleteButton } from "../../components/AppBulkDeleteButton";
import { FeedbackTypeChip, memberSearchHref } from "./feedbackTypes";
import { colors, fontFamilyMono, radius, shadows } from "../../data/tokens";
import { initials } from "../../util/text";
import { useMemberName } from "../../util/useMemberName";
import { determineUserReadableRole } from "../users/UserUtils";

// Remetente no estilo caixa de entrada: avatar + nome + identificador.
// O campo "email" pode conter e-mail ou CPF (identificador do sócio).
// Clicar no remetente abre o cadastro do sócio (link do botão antigo).
const SenderCell = () => {
  const record: any = useRecordContext();
  const email = String(record?.email || "").trim();
  // Nome real do sócio, buscado do cadastro (estilo Gmail); cai para o
  // nome gravado no contato e, por fim, para o próprio identificador
  const memberName = useMemberName(email);
  if (!record) return null;
  const feedbackName = String(record.name || "").trim();
  const isEmail = email.includes("@");
  const displayName = memberName || feedbackName || email || "—";
  const hasRealName = Boolean(
    displayName && email && displayName.toLowerCase() !== email.toLowerCase()
  );
  const content = (
    <>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 12,
          fontWeight: 700,
          backgroundColor: colors.bgContent,
          color: colors.primaryDark,
        }}
      >
        {hasRealName
          ? initials(displayName)
          : isEmail
          ? email[0].toUpperCase()
          : null}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          className="sender-name"
          noWrap
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: colors.primaryDark,
            maxWidth: 200,
          }}
        >
          {displayName}
        </Typography>
        {/* Identificador embaixo só quando difere do nome (evita repetição) */}
        {hasRealName ? (
          <Typography
            noWrap
            sx={{
              fontSize: 11.5,
              color: colors.textMuted,
              maxWidth: 200,
              fontFamily: isEmail ? undefined : fontFamilyMono,
            }}
          >
            {email}
          </Typography>
        ) : null}
      </Box>
    </>
  );
  if (!email) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        {content}
      </Box>
    );
  }
  return (
    <Tooltip title="Ver cadastro do sócio">
      <Box
        component="a"
        href={memberSearchHref(email)}
        onClick={(e: any) => e.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          textDecoration: "none",
          color: "inherit",
          "&:hover .sender-name": { textDecoration: "underline" },
        }}
      >
        {content}
      </Box>
    </Tooltip>
  );
};

// Prévia da mensagem em linha única, como assunto de e-mail.
// A largura vem da célula (coluna flexível) — ver "column-message" no sx.
const MessagePreview = () => {
  const record: any = useRecordContext();
  if (!record) return null;
  return (
    <Typography noWrap sx={{ fontSize: 13, color: colors.textMuted }}>
      {record.message}
    </Typography>
  );
};

// Toolbar da lista: Criar/Exportar encostados na borda esquerda da
// tabela; a navegação compacta continua à direita
const ListActions = () => (
  <TopToolbar
    sx={{
      alignItems: "center",
      width: "100%",
      justifyContent: "space-between",
    }}
  >
    <Box sx={{ display: "flex", gap: 1 }}>
      <CreateButton />
      <ExportButton />
    </Box>
    <ListPageIndicator />
  </TopToolbar>
);

export const FeedbacksList = () => {
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";
  // Sem filtro de busca por enquanto: o backend ignora o filtro "email"
  // neste endpoint (só aplica q/name) — o campo nunca filtrou nada.
  // Religar junto com a correção do feedbacks.service (frente backend).

  return (
    <>
      <PageTopBreadcrumb
        title="Contatos"
        firstPath="/#/feedbacks/crud"
        actionType="create"
      />
      <List
        actions={<ListActions />}
        sort={{ field: "createdAt", order: "DESC" }}
        perPage={20}
        // Mantém a página atual visível enquanto a próxima carrega
        // (sem "piscada" de tabela vazia ao navegar)
        queryOptions={{ keepPreviousData: true }}
        pagination={<Pagination rowsPerPageOptions={[10, 20, 50]} />}
        sx={{
          // Busca, tabela e paginação dentro do mesmo cartão branco
          "& .RaList-main": {
            backgroundColor: colors.white,
            borderRadius: `${radius.lg}px`,
            boxShadow: shadows.card,
            padding: "4px 20px 8px",
          },
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          // Toolbar no fluxo normal: ações e navegação de página à
          // direita (anula o hack antigo do App.css que as posicionava
          // em absolute sobre o conteúdo)
          "& .RaList-actions": { alignItems: "center" },
          "& .RaList-actions .MuiToolbar-root": {
            position: "static",
            gap: 1,
          },
        }}
      >
        <Datagrid
          rowClick="show"
          bulkActionButtons={isAdmin ? <AppBulkDeleteButton /> : false}
          sx={{
            "& .RaDatagrid-headerCell": {
              fontSize: 11.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: colors.textMuted,
              backgroundColor: colors.bgSubtle,
              paddingTop: 1,
              paddingBottom: 1,
            },
            "& .RaDatagrid-rowCell": { paddingTop: 0.75, paddingBottom: 0.75 },
            // Coluna da mensagem absorve o espaço restante e trunca
            // (maxWidth 0 + width 100% = truque de tabela flexível);
            // assim a tabela nunca fica mais larga que o cartão
            "& .column-message": { maxWidth: 0, width: "100%" },
            // Tipo à esquerda no cabeçalho E nas células (chips de
            // larguras diferentes centralizados pareciam soltos)
            "& .column-type": { textAlign: "left" },
          }}
        >
          <FunctionField label="Membro" render={() => <SenderCell />} />
          <FunctionField
            label="Mensagem"
            source="message"
            sortable={false}
            render={() => <MessagePreview />}
          />
          {/* source="type" dá a classe .column-type às células e ao
              cabeçalho (o sortable continua desligado: o backend ignora
              ordenação nesta tela) */}
          <FunctionField
            label="Tipo"
            source="type"
            sortable={false}
            render={(record: any) => <FeedbackTypeChip type={record.type} />}
          />
          <DateField
            source="createdAt"
            locales="pt-BR"
            label="Data"
            // O backend fixa a ordem (mais recentes primeiro) e ignora o
            // sort da requisição — sem seta para não prometer o que não há
            sortable={false}
            textAlign="right"
            sx={{
              fontSize: 12.5,
              color: colors.textMuted,
              whiteSpace: "nowrap",
            }}
          />
          {isAdmin && (
            <FunctionField
              label="Ações"
              textAlign="right"
              render={() => <RaRowActions />}
            />
          )}
        </Datagrid>
      </List>
    </>
  );
};
